import { useState, useCallback } from 'react'
import { useDropzone } from 'react-dropzone'
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage'
import { httpsCallable } from 'firebase/functions'
import { storage, functions } from '../../lib/firebase'
import './SongEditor.css'

export default function SongEditor({ song, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    albumId: song.albumId,
    title: song.title || '',
    artist: song.artist || '',
    trackNumber: song.trackNumber || 1,
    mp3Url: song.mp3Url || '',
    wavDownloadUrl: song.wavDownloadUrl || '',
    durationSeconds: song.durationSeconds || 0,
    lyrics: song.lyrics || '',
    id: song.id || null
  })

  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadingWav, setUploadingWav] = useState(false)
  const [wavUploadProgress, setWavUploadProgress] = useState(0)
  const [wavStatus, setWavStatus] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const onDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0]
    if (!file) return

    setUploading(true)
    setUploadProgress(0)
    setError(null)

    try {
      const timestamp = Date.now()
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      const storageRef = ref(storage, `songs/${timestamp}-${safeName}`)

      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on('state_changed',
        (snapshot) => {
          const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          setUploadProgress(pct)
        },
        (err) => {
          setError(err.message)
          setUploading(false)
        },
        async () => {
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
          handleChange('mp3Url', downloadURL)

          // Try to get duration from the audio file
          const audio = new Audio(downloadURL)
          audio.addEventListener('loadedmetadata', () => {
            if (audio.duration && isFinite(audio.duration)) {
              handleChange('durationSeconds', Math.round(audio.duration))
            }
          })

          setUploading(false)
        }
      )
    } catch (err) {
      setError(err.message)
      setUploading(false)
    }
  }, [])

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'audio/mpeg': ['.mp3'] },
    multiple: false,
    disabled: uploading
  })

  const onDropWav = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles[0]
    if (!file) return

    setUploadingWav(true)
    setWavUploadProgress(0)
    setWavStatus('Uploading...')
    setError(null)

    try {
      // Upload to Firebase Storage first (as staging)
      const timestamp = Date.now()
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_')
      const filePath = `songs/wav/${timestamp}-${safeName}`
      const storageRef = ref(storage, filePath)

      const uploadTask = uploadBytesResumable(storageRef, file)

      uploadTask.on('state_changed',
        (snapshot) => {
          const pct = (snapshot.bytesTransferred / snapshot.totalBytes) * 80 // 0-80% for upload
          setWavUploadProgress(pct)
        },
        (err) => {
          setError(err.message)
          setUploadingWav(false)
          setWavStatus('')
        },
        async () => {
          // Upload complete, now finalize (backup to Dropbox, get link, cleanup)
          setWavStatus('Syncing to Dropbox...')
          setWavUploadProgress(85)

          try {
            const finalizeWavUpload = httpsCallable(functions, 'finalizeWavUpload')
            const result = await finalizeWavUpload({ filePath })

            setWavUploadProgress(100)

            if (result.data.url) {
              handleChange('wavDownloadUrl', result.data.url)
              setWavStatus('')
            } else {
              throw new Error('No URL returned')
            }
          } catch (finalizeErr) {
            console.error('Error finalizing WAV upload:', finalizeErr)
            // Fall back to Firebase Storage URL if Dropbox fails
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref)
            handleChange('wavDownloadUrl', downloadURL)
            setWavStatus('Using temporary link (Dropbox sync failed)')
          }

          setUploadingWav(false)
        }
      )
    } catch (err) {
      setError(err.message)
      setUploadingWav(false)
      setWavStatus('')
    }
  }, [])

  const {
    getRootProps: getWavRootProps,
    getInputProps: getWavInputProps,
    isDragActive: isWavDragActive
  } = useDropzone({
    onDrop: onDropWav,
    accept: { 'audio/wav': ['.wav'], 'audio/x-wav': ['.wav'] },
    multiple: false,
    disabled: uploadingWav
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSaving(true)
    setError(null)

    try {
      await onSave(formData)
    } catch (err) {
      setError(err.message)
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <h2>{formData.id ? 'Edit Song' : 'Add Song'}</h2>

        <form onSubmit={handleSubmit}>
          {error && <div className="error-banner">{error}</div>}

          <div className="form-group">
            <label htmlFor="song-title">Title *</label>
            <input
              id="song-title"
              type="text"
              value={formData.title}
              onChange={(e) => handleChange('title', e.target.value)}
              required
              placeholder="Song title"
            />
          </div>

          <div className="form-group">
            <label htmlFor="song-artist">Artist</label>
            <input
              id="song-artist"
              type="text"
              value={formData.artist}
              onChange={(e) => handleChange('artist', e.target.value)}
              placeholder="Leave blank to use album artist"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label htmlFor="track-number">Track #</label>
              <input
                id="track-number"
                type="number"
                min="1"
                value={formData.trackNumber}
                onChange={(e) => handleChange('trackNumber', parseInt(e.target.value) || 1)}
              />
            </div>

            <div className="form-group">
              <label htmlFor="duration">Duration (seconds)</label>
              <input
                id="duration"
                type="number"
                min="0"
                value={formData.durationSeconds}
                onChange={(e) => handleChange('durationSeconds', parseInt(e.target.value) || 0)}
                placeholder="Auto-detected from MP3"
              />
            </div>
          </div>

          <div className="form-group">
            <label>MP3 File (for streaming)</label>
            {formData.mp3Url ? (
              <div className="file-preview">
                <span className="file-icon">🎵</span>
                <span className="file-url">{formData.mp3Url.split('/').pop().split('?')[0]}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleChange('mp3Url', '')}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div
                {...getRootProps()}
                className={`dropzone ${isDragActive ? 'active' : ''} ${uploading ? 'uploading' : ''}`}
              >
                <input {...getInputProps()} />
                {uploading ? (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${uploadProgress}%` }} />
                    </div>
                    <span>{Math.round(uploadProgress)}%</span>
                  </div>
                ) : isDragActive ? (
                  <p>Drop MP3 here...</p>
                ) : (
                  <p>Drag & drop an MP3, or click to select</p>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label>WAV File (for downloads)</label>
            {formData.wavDownloadUrl ? (
              <div className="file-preview">
                <span className="file-icon">🎧</span>
                <span className="file-url">{formData.wavDownloadUrl.split('/').pop().split('?')[0]}</span>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm"
                  onClick={() => handleChange('wavDownloadUrl', '')}
                >
                  Remove
                </button>
              </div>
            ) : (
              <div
                {...getWavRootProps()}
                className={`dropzone ${isWavDragActive ? 'active' : ''} ${uploadingWav ? 'uploading' : ''}`}
              >
                <input {...getWavInputProps()} />
                {uploadingWav ? (
                  <div className="upload-progress">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{ width: `${wavUploadProgress}%` }} />
                    </div>
                    <span>{wavUploadProgress < 100 ? `${Math.round(wavUploadProgress)}%` : wavStatus}</span>
                  </div>
                ) : isWavDragActive ? (
                  <p>Drop WAV here...</p>
                ) : (
                  <p>Drag & drop a WAV, or click to select</p>
                )}
              </div>
            )}
            {wavStatus && !uploadingWav && (
              <p className="help-text">{wavStatus}</p>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="song-lyrics">Lyrics (optional)</label>
            <textarea
              id="song-lyrics"
              value={formData.lyrics}
              onChange={(e) => handleChange('lyrics', e.target.value)}
              placeholder="Enter song lyrics..."
              rows={8}
            />
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn btn-ghost"
              onClick={onCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || uploading || uploadingWav}
            >
              {saving ? 'Saving...' : 'Save Song'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}
